import React, { useState } from "react";
import { motion } from "framer-motion";
import Modal from "./Modal";
import ReCAPTCHA from "react-google-recaptcha";

interface FormData {
  ageGroup: "" | "8-11" | "12-14";
  turnus: "" | "a" | "b" | "c";
  firstName: string;
  lastName: string;
  birthDate: string;
  address: string;
  parentFirstName: string;
  parentLastName: string;
  parentPhone: string;
  parentEmail: string;
  allergies: string;
  needsMedication: string;
  needsHygiene: string;
  specialDiet: string;
  insuranceConsent: boolean;
  departure: "" | "parents" | "other";
  departureOther: string;
  dataProtectionConsent: boolean;
}

/* ---------------------------------------------------------
   TS2448 FIX ⇒ errors musia pokrývať aj captcha
--------------------------------------------------------- */
type FormErrors = Partial<Record<keyof FormData | "captcha", string>>;

type Step = {
  title: string;
  content: JSX.Element;
};

const personalFields: { label: string; name: keyof FormData; type: string }[] = [
  { label: "Meno", name: "firstName", type: "text" },
  { label: "Priezvisko", name: "lastName", type: "text" },
  { label: "Dátum narodenia", name: "birthDate", type: "date" },
  { label: "Bydlisko", name: "address", type: "text" },
];

const parentFields: { label: string; name: keyof FormData; type: string }[] = [
  { label: "Meno rodiča", name: "parentFirstName", type: "text" },
  { label: "Priezvisko rodiča", name: "parentLastName", type: "text" },
  { label: "Telefón", name: "parentPhone", type: "tel" },
  { label: "E‑mail", name: "parentEmail", type: "email" },
];

const healthFields: { label: string; name: keyof FormData; type: string }[] = [
  { label: "Alergie (alebo 'žiadne')", name: "allergies", type: "text" },
  { label: "Potrebné lieky (alebo 'žiadne')", name: "needsMedication", type: "text" },
  { label: "Hygienické potreby (alebo 'žiadne')", name: "needsHygiene", type: "text" },
  { label: "Špeciálna strava (alebo 'žiadne')", name: "specialDiet", type: "text" },
];

const RECAPTCHA_SITE_KEY = "6LcEzDgrAAAAAN4aHwEFrPswaB3crLKPkoLYZmMO";

const Registration: React.FC = () => {
  const [step, setStep] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const [formData, setFormData] = useState<FormData>({
    ageGroup: "",
    turnus: "",
    firstName: "",
    lastName: "",
    birthDate: "",
    address: "",
    parentFirstName: "",
    parentLastName: "",
    parentPhone: "",
    parentEmail: "",
    allergies: "",
    needsMedication: "",
    needsHygiene: "",
    specialDiet: "",
    insuranceConsent: false,
    departure: "",
    departureOther: "",
    dataProtectionConsent: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const formatAgeGroup = (ag: string) => ag.replace(/-/g, " - ");

  const handleFormChange: React.ChangeEventHandler<any> = (e) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const name = target.name as keyof FormData;
    const value: string | boolean = target.type === "checkbox" ? (target as HTMLInputElement).checked : target.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };


  /* --------------------------- VALIDÁCIA --------------------------- */
  const validateStep = () => {
    const newErrors: FormErrors = {};

    if (step === 1) {
      if (!formData.ageGroup) newErrors.ageGroup = "Musíš vybrať vekovú kategóriu!";
      if (!formData.turnus) newErrors.turnus = "Musíš vybrať turnus!";
    }

    if (step === 2) {
      if (formData.firstName.length < 2) newErrors.firstName = "Meno musí mať aspoň 2 znaky";
      if (formData.lastName.length < 2) newErrors.lastName = "Priezvisko musí mať aspoň 2 znaky";
      if (!formData.birthDate) newErrors.birthDate = "Dátum narodenia je povinný";
      if (formData.address.length < 5) newErrors.address = "Bydlisko musí mať aspoň 5 znakov";
    }

    if (step === 3) {
      if (formData.parentFirstName.length < 2) newErrors.parentFirstName = "Meno rodiča musí mať aspoň 2 znaky";
      if (formData.parentLastName.length < 2) newErrors.parentLastName = "Priezvisko rodiča musí mať aspoň 2 znaky";
      if (!/^\+?\d{9,12}$/.test(formData.parentPhone)) newErrors.parentPhone = "Platné telefónne číslo (9–12 číslic)";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.parentEmail)) newErrors.parentEmail = "Platný email je povinný";
    }

    if (step === 4) {
      healthFields.forEach(field => {
        const value = formData[field.name] as string;
        if (!value) newErrors[field.name] = "Prosím vyplň alebo napíš 'žiadne'";
        else if (value.toLowerCase() !== "žiadne" && value.length < 5) newErrors[field.name] = `Prosím opíš ${field.label.toLowerCase()} alebo napíš 'žiadne'`;
      });
    }

    if (step === 7 && !formData.insuranceConsent) {
      newErrors.insuranceConsent = "Musíš potvrdiť prehlásenie";
    }

    if (step === 8) {
      if (!formData.departure) newErrors.departure = "Vyber spôsob odchodu";
      if (formData.departure === "other" && !formData.departureOther.trim()) newErrors.departureOther = "Uveď, kto";
    }

    if (step === 10) {
      if (!formData.dataProtectionConsent) newErrors.dataProtectionConsent = "Musíš súhlasiť so spracovaním údajov";
      if (!captchaToken) newErrors.captcha = "Potvrďte, že nie ste robot.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const next = () => validateStep() && setStep(s => s + 1);
  const prev = () => setStep(s => s - 1);

  /* -------------------------- SUBMIT -------------------------- */
  const handleFinish = async () => {
    if (!validateStep()) return;
    setSending(true);
    try {
      const resp = await fetch("https://www.dieceznytabor.sk/admin/public/api/send-mail", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, captchaToken }),
      });
      if (!resp.ok) throw new Error("Sieťová chyba");
      setShowModal(true);
    } catch (err) {
      console.error("Chyba pri odosielaní:", err);
      alert("Nepodarilo sa odoslať prihlášku. Skúste to neskôr.");
    } finally {
      setSending(false);
    }
  };

  // Framer Motion variants
  const formVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
    exit: { opacity: 0, y: -50, transition: { duration: 0.8, ease: "easeIn" } },
  };
  const inputVariants = {
    focus: {
      scale: 1.05,
      borderColor: "#FF5F00",
      boxShadow: "0 0 15px rgba(255,95,0,0.6)",
      transition: { duration: 0.3 },
    },
    hover: { scale: 1.02, transition: { duration: 0.2 } },
  };
  const buttonVariants = {
    hover: {
      scale: 1.15,
      rotate: step === 1 ? 5 : -5,
      boxShadow: "0 10px 20px rgba(255,95,0,0.4)",
      transition: { type: "spring", stiffness: 300 },
    },
    tap: { scale: 0.9 },
  };
  const rocketVariants = {
    animate: {
      y: [0, -20, 0],
      rotate: [0, 5, -5, 0],
      transition: { duration: 2, repeat: Infinity, ease: "easeInOut" },
    },
  };
  const plushToyVariants = {
    animate: {
      scale: [1, 1.1, 1],
      rotate: [0, 10, -10, 0],
      transition: { duration: 1.5, repeat: Infinity, ease: "easeInOut" },
    },
  };
  const superheroVariants = {
    animate: {
      y: [0, -15, 0],
      scale: [1, 1.05, 1],
      transition: { duration: 1.8, repeat: Infinity, ease: "easeInOut" },
    },
  };
  const campfireVariants = {
    animate: {
      scale: [1, 1.1, 1],
      opacity: [1, 0.8, 1],
      transition: { duration: 2, repeat: Infinity, ease: "easeInOut" },
    },
  };
  const speakerVariants = {
    animate: {
      rotate: [0, 5, -5, 0],
      scale: [1, 1.05, 1],
      transition: { duration: 1.5, repeat: Infinity, ease: "easeInOut" },
    },
  };
  const chatBubbleVariants = {
    animate: {
      y: [0, -10, 0],
      opacity: [1, 0.7, 1],
      transition: { duration: 1.2, repeat: Infinity, ease: "easeInOut" },
    },
  };

  const steps: Step[] = [
    {
      title: "Veková kategória a turnus",
      content: (
        <div className="space-y-4 sm:space-y-8">
       
          <h3
            className="text-2xl sm:text-4xl md:text-5xl font-bold uppercase text-navy text-center"
            style={{ fontFamily: "Baloo 2", fontWeight: 800 }}
          >
            Vyber si vekovú kategóriu a turnus
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <motion.button
              onClick={() => setFormData((f) => ({ ...f, ageGroup: "8-11" }))}
              className={`py-2 sm:py-6 px-4 sm:px-8 rounded-3xl font-bold tracking-wide shadow-2xl text-base sm:text-2xl relative overflow-hidden transition-colors duration-300 ${
                formData.ageGroup === "8-11"
                  ? "bg-green-500 text-navy"
                  : "bg-navy/10 text-green-700"
              } border-4 border-green-500 w-full focus:ring-4 focus:ring-orange-400 focus:outline-none`}
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              style={{ fontFamily: "'Mystery Quest', cursive" }}
            >
              <motion.div
                className="absolute inset-0 bg-pink-200/50"
                initial={{ scale: 0 }}
                whileHover={{ scale: 1 }}
                transition={{ duration: 0.3 }}
              />
              8 až 11 rokov
            </motion.button>
            <motion.button
              onClick={() => setFormData((f) => ({ ...f, ageGroup: "12-14" }))}
              className={`py-2 sm:py-6 px-4 sm:px-8 rounded-3xl font-bold tracking-wide shadow-2xl text-base sm:text-2xl relative overflow-hidden transition-colors duration-300 ${
                formData.ageGroup === "12-14"
                  ? "bg-navy text-pink-400"
                  : "bg-navy text-cream"
              } border-4 border-pink-400 w-full focus:ring-4 focus:ring-orange-400 focus:outline-none`}
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              style={{ fontFamily: "'Rubik Vinyl', sans-serif" }}
            >
              <motion.div
                className="absolute inset-0 bg-neon-green/30"
                initial={{ scale: 0 }}
                whileHover={{ scale: 1 }}
                transition={{ duration: 0.3 }}
              />
              12 až 14 rokov
            </motion.button>
          </div>
          {formData.ageGroup && (
            <p className="text-navy text-base sm:text-lg text-center">
              Vybraná veková kategória:{" "}
              <span className="font-bold">{formatAgeGroup(formData.ageGroup)} rokov
              </span>
            </p>
          )}
          {errors.ageGroup && (
            <p className="text-primary text-sm sm:text-lg text-center">
              {errors.ageGroup}
            </p>
          )}
          <div>
            <label
              className="block text-base sm:text-xl md:text-2xl font-bold uppercase text-navy mb-1 sm:mb-2"
              style={{ fontFamily: "Baloo 2", fontWeight: 600 }}
            >
              Vyber turnus
            </label>
            <motion.select
              name="turnus"
              value={formData.turnus}
              onChange={handleFormChange}
              className="w-full p-2 sm:p-5 border-2 border-navy rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-400 bg-cream/90 text-sm sm:text-lg"
              variants={inputVariants}
              whileFocus="focus"
              whileHover="hover"
            >
              <option value="">Vyber turnus...</option>
              <option value="a">od 1. do 6. júla 2025</option>
              <option value="b">od 5. do 9. augusta 2025</option>
              <option value="c">od 10. do 15. augusta 2025</option>

            </motion.select>
            {errors.turnus && (
              <p className="text-primary text-sm sm:text-lg mt-1 sm:mt-2">
                {errors.turnus}
              </p>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Osobné údaje účastníka",
      content: (
        <div className="space-y-4 sm:space-y-8">
          {personalFields.map((f) => (
            <motion.div key={f.name} variants={inputVariants}>
              <label
                className="block text-base sm:text-xl md:text-2xl font-bold uppercase text-navy mb-1 sm:mb-2"
                style={{ fontFamily: "Baloo 2", fontWeight: 600 }}
              >
                {f.label}
              </label>
              <motion.input
                type={f.type}
                name={f.name}
                value={formData[f.name] as string}
                onChange={handleFormChange}
                className="w-full p-2 sm:p-5 border-2 border-navy rounded-xl focus:outline-none bg-cream/90 text-sm sm:text-lg"
                whileFocus="focus"
                whileHover="hover"
              />
              {errors[f.name] && (
                <p className="text-primary text-sm sm:text-lg mt-1 sm:mt-2">
                  {errors[f.name]}
                </p>
              )}
            </motion.div>
          ))}
        </div>
      ),
    },
    {
      title: "Údaje zákonného zástupcu",
      content: (
        <div className="space-y-4 sm:space-y-8">
          {parentFields.map((f) => (
            <motion.div key={f.name} variants={inputVariants}>
              <label
                className="block text-base sm:text-xl md:text-2xl font-bold uppercase text-navy mb-1 sm:mb-2"
                style={{ fontFamily: "Baloo 2", fontWeight: 600 }}
              >
                {f.label}
              </label>
              <motion.input
                type={f.type}
                name={f.name}
                value={formData[f.name] as string}
                onChange={handleFormChange}
                className="w-full p-2 sm:p-5 border-2 border-navy rounded-xl focus:outline-none bg-cream/90 text-sm sm:text-lg"
                whileFocus="focus"
                whileHover="hover"
              />
              {errors[f.name] && (
                <p className="text-primary text-sm sm:text-lg mt-1 sm:mt-2">
                  {errors[f.name]}
                </p>
              )}
            </motion.div>
          ))}
        </div>
      ),
    },
    {
      title: "Zdravotné údaje",
      content: (
        <div className="space-y-4 sm:space-y-8">
          {healthFields.map((f) => (
            <motion.div key={f.name} variants={inputVariants}>
              <label
                className="block text-base sm:text-xl md:text-2xl font-bold uppercase text-navy mb-1 sm:mb-2"
                style={{ fontFamily: "Baloo 2", fontWeight: 600 }}
              >
                {f.label}
              </label>
              <motion.input
                type={f.type}
                name={f.name}
                value={formData[f.name] as string}
                onChange={handleFormChange}
                className="w-full p-2 sm:p-5 border-2 border-navy rounded-xl focus:outline-none bg-cream/90 text-sm sm:text-lg"
                whileFocus="focus"
                whileHover="hover"
              />
              {errors[f.name] && (
                <p className="text-primary text-sm sm:text-lg mt-1 sm:mt-2">
                  {errors[f.name]}
                </p>
              )}
            </motion.div>
          ))}
        </div>
      ),
    },
    {
      title: "5. Potrebné priniesť",
      content: (
        <div className="bg-cream p-4 rounded-xl border-2 border-navy text-navy text-base sm:text-lg space-y-2">
          {[
            "Prefotenú kartičku poistenca",
            "Fľašu na vodu",
            "Prezuvky",
            "Šiltovku",
            "Vreckovky",
            "Ruksak",
            "Hygienické potreby",
            "Pršiplášť",
            "Veci na prezlečenie",
            "Lieky (ak ich dieťa užíva)",
          ].map((item, index) => (
            <p key={index} className="font-bold uppercase">
              {item}
            </p>
          ))}
        </div>
      ),
    },
    {
      title: "6. Strava",
      content: (
        <div className="bg-cream p-4 rounded-xl border-2 border-navy text-navy text-base sm:text-lg space-y-4">
          <p>
            V priebehu tábora bude zabezpečená strava pre všetky deti podľa denného režimu, ktorý bude zahŕňať: raňajky, desiaty, obedy, olovranty a večere. 
          </p>
          <hr className="border-navy/50" />
          <p>
            Strava bude prispôsobená vekovým a nutričným potrebám detí, pričom
            sa bude prihliadať aj na prípadné diéty alebo potravinové alergie.
          </p>
          <hr className="border-navy/50" />
          <p>
            V prípade, že má vaše dieťa špecifické požiadavky na stravu (napr.
            alergie, vegetariánska strava, bezlepková diéta a pod.), prosíme
            vás, aby ste tieto informácie uviedli v príslušnej časti
            prihlasovacieho formulára.
          </p>
        </div>
      ),
    },
    {
      title: "7. Prehlásenie",
      content: (
        <div className="space-y-4 sm:space-y-8">
          <p className="text-navy text-base sm:text-lg">
            Beriem na vedomie, že účastník ( moje dieťa ) nie je organizátorom
            poistený (úraz, krádež).
          </p>
          <motion.div
            className="flex items-center space-x-2"
            whileHover="hover"
          >
            <motion.input
              type="checkbox"
              name="insuranceConsent"
              checked={formData.insuranceConsent}
              onChange={handleFormChange}
              className="w-5 h-5 sm:w-8 sm:h-8 border-2 border-navy rounded"
            />
            <span className="text-navy text-base sm:text-lg">Súhlasím</span>
          </motion.div>
          {errors.insuranceConsent && (
            <p className="text-primary text-sm sm:text-lg">
              {errors.insuranceConsent}
            </p>
          )}
        </div>
      ),
    },
    {
      title: "8. Odchod dieťaťa",
      content: (
        <div className="space-y-4 sm:space-y-8">
          <p className="text-navy text-base sm:text-lg">
            Potvrdzujem, že moje dieťa bude odchádzať z tábora:
          </p>
          <div className="flex flex-col space-y-2 mb-2">
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                name="departure"
                value="parents"
                checked={formData.departure === "parents"}
                onChange={handleFormChange}
                className="w-5 h-5 sm:w-8 sm:h-8 border-2 border-navy rounded"
              />
              <span className="text-navy text-base sm:text-lg">
                V sprievode rodičov
              </span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                name="departure"
                value="other"
                checked={formData.departure === "other"}
                onChange={handleFormChange}
                className="w-5 h-5 sm:w-8 sm:h-8 border-2 border-navy rounded"
              />
              <span className="text-navy text-base sm:text-lg">
                Prosím uveďte
              </span>
            </label>
            {formData.departure === "other" && (
              <motion.input
                type="text"
                name="departureOther"
                placeholder="Kto…"
                value={formData.departureOther}
                onChange={handleFormChange}
                className="w-full p-2 sm:p-5 border-2 border-navy rounded-xl focus:outline-none bg-cream/90 text-sm sm:text-lg"
                whileFocus="focus"
                whileHover="hover"
              />
            )}
          </div>
          {errors.departure && (
            <p className="text-primary text-sm sm:text-lg">
              {errors.departure}
            </p>
          )}
          {errors.departureOther && (
            <p className="text-primary text-sm sm:text-lg">
              {errors.departureOther}
            </p>
          )}
        </div>
      ),
    },
    {
      title: "9. Platobné údaje",
      content: (
        <div className="space-y-4 sm:space-y-8 mt-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-2">
              <p>
                <strong>Občianske združenie:</strong> Svätý Vendelín
              </p>
              <p>
                <strong>Adresa:</strong> Zubák 140, 020 64 Zubák
              </p>
              <p>
                <strong>IČO:</strong> 45783128
              </p>
              <p>
                <strong>DIČ:</strong> 2120155169
              </p>
            </div>
            <div className="space-y-2">
              <p>
                <strong>Bankové spojenie:</strong> ČSOB – Československá
                obchodná banka, a.s.
              </p>
              <p>
                <strong>Kód banky:</strong> 7500
              </p>
              <p>
                <strong>SWIFT (BIC):</strong> CEKOSKBX
              </p>
              <p>
                <strong>IBAN:</strong> SK09 7500 0000 0040 2249 0459
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <p>
              <strong>Cena za Tábor:</strong> 219,99 € alebo 249,99 €
            </p>
            <p className="text-sm text-gray-600">
              Prosím, pri platbe uveďte tieto údaje:
            </p>
            <p>
              <strong>Poznámka pre príjemcu:</strong> {formData.firstName}{" "}
              {formData.lastName}
            </p>
          </div>
        </div>
      ),
    },
    {
      title: "10. Ochrana osobných údajov",
      content: (
        <div className="space-y-4 sm:space-y-8">
          <p className="text-navy text-base sm:text-lg">
            Podľa § 7 zákona č. 428/2002 Z.z. o ochrane osobných údajov v
            platnom znení súhlasím so spracovaním osobných údajov môjho dieťaťa
            a mojich osobných údajov v rozsahu uvedenom v prihláške a v súlade s
            pravidlami spacovania osobných údajov podľa pravidiel GDPR na účely
            organizácie a realizácie tábora, zabezpečenia bezpečnosti a zdravia
            dieťaťa počas pobytu na tábore, ako aj pre administratívne účely
            súvisiace s organizovaním tábora (napr. potvrdenie o prihlásení,
            komunikácia pred a počas tábora). Tento súhlas je udelený na dobu
            nevyhnutnú na účely spracovania údajov v súvislosti s organizovaním
            tábora a na obdobie trvania právnych nárokov, ktoré môžu vyplývať z
            účasti dieťaťa na tábore. Zároveň potvrdzujem, že som bol/a
            informovaný/á o právach týkajúcich sa ochrany osobných údajov,
            vrátane práva na prístup k údajom, ich opravu, vymazanie alebo
            obmedzenie spracovania a práva odvolať tento súhlas kedykoľvek, bez
            negatívnych dôsledkov. Tento súhlas je udelený slobodne a
            dobrovoľne, s plným porozumením účelu spracovania údajov.
          </p>
          <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} onChange={setCaptchaToken} />
          {errors.captcha && (
            <p className="text-primary text-sm sm:text-lg mt-2">{errors.captcha}</p>
          )}
          <motion.div
            className="flex items-center space-x-2"
            whileHover="hover"
          >
            <motion.input
              type="checkbox"
              name="dataProtectionConsent"
              checked={formData.dataProtectionConsent}
              onChange={handleFormChange}
              className="w-5 h-5 sm:w-8 sm:h-8 border-2 border-navy rounded"
            />
            <span className="text-navy text-base sm:text-lg">Súhlasím</span>

          </motion.div>
          {errors.dataProtectionConsent && (
            <p className="text-primary text-sm sm:text-lg">
              {errors.dataProtectionConsent}
            </p>
          )}
        </div>
      ),
    },
  ];

  return (
    <section
      id="registration"
      className="min-h-[100vh] sm:min-h-screen bg-gradient-to-br from-cream to-white w-full overflow-y-auto py-6 sm:py-12 relative"
    >
      <motion.h2
        className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-center mb-4 sm:mb-8 uppercase tracking-tight drop-shadow-2xl text-navy"
        style={{ fontFamily: "Baloo 2", fontWeight: 800 }}
        initial={{ opacity: 0, y: 100 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2 }}
        viewport={{ once: true }}
      >
        Prihláška
      </motion.h2>

      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex flex-col md:flex-row items-center justify-center gap-6 sm:gap-8 relative">
          {/* Left Side Assets */}
          {formData.ageGroup === "8-11" && (
            <div className="hidden md:flex flex-col items-center space-y-6 absolute left-0 transform -translate-x-1/6">
              <motion.img
                src="./assets/raketa-01.svg"
                alt="Rocket"
                className="h-16 sm:h-20 md:h-48"
                variants={rocketVariants}
                animate="animate"
              />
            </div>
          )}
          {formData.ageGroup === "12-14" && (
            <div className="hidden md:flex flex-col items-center space-y-32 absolute left-0 transform -translate-x-1/6">
              <motion.img
                src="./assets/reprak-01.svg"
                alt="Speaker"
                className="h-16 sm:h-20 md:h-48"
                variants={speakerVariants}
                animate="animate"
              />
              <motion.img
                src="./assets/chat-01.svg"
                alt="Chat Bubble"
                className="h-16 sm:h-20 md:h-48"
                variants={chatBubbleVariants}
                animate="animate"
              />
            </div>
          )}

          {/* Form */}
          <motion.div
            key={step}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={formVariants}
            className="bg-white rounded-3xl shadow-2xl border-4 border-primary p-4 sm:p-8 md:p-12 max-w-[90%] sm:max-w-2xl md:max-w-3xl w-full relative z-10"
          >
            <motion.div
              className="absolute -top-4 sm:-top-6 -left-4 sm:-left-6 w-8 sm:w-12 h-8 sm:h-12 bg-primary rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
            />
            <motion.div
              className="absolute -bottom-4 sm:-bottom-6 -right-4 sm:-right-6 w-8 sm:w-12 h-8 sm:h-12 bg-secondary rounded-full"
              animate={{ rotate: -360 }}
              transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
            />

            <h3
              className="text-xl sm:text-3xl md:text-4xl font-bold uppercase text-navy mb-3 sm:mb-6 text-center"
              style={{ fontFamily: "Baloo 2", fontWeight: 800 }}
            >
              {steps[step - 1].title}
            </h3>
            {steps[step - 1].content}

            <div className="flex flex-col sm:flex-row justify-between mt-4 sm:mt-8 space-y-3 sm:space-y-0 sm:space-x-4">
              {step > 1 && (
                <motion.button
                  onClick={prev}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  className="bg-navy text-cream py-2 sm:py-4 px-4 sm:px-8 rounded-xl font-bold uppercase tracking-wide shadow-lg text-sm sm:text-lg w-full sm:w-auto"
                >
                  Späť
                </motion.button>
              )}
              {step < steps.length ? (
                <motion.button
                  onClick={next}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  className="bg-primary text-cream py-2 sm:py-4 px-4 sm:px-8 rounded-xl font-bold uppercase tracking-wide shadow-lg text-sm sm:text-lg w-full sm:w-auto"
                >
                  Ďalej
                </motion.button>
              ) : (
                <motion.button
                  onClick={handleFinish}
                  disabled={sending}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  className="bg-green-500 text-cream py-2 sm:py-4 px-4 sm:px-8 rounded-xl font-bold uppercase tracking-wide shadow-lg text-sm sm:text-lg w-full sm:w-auto"
                >
                  {sending ? "Odosielam…" : "Odoslať"}
                </motion.button>
              )}
            </div>
          </motion.div>

          {/* Right Side Assets */}
          {formData.ageGroup === "8-11" && (
            <div className="hidden md:flex flex-col items-center space-y-32 absolute right-0 transform translate-x-1/6">
              <motion.img
                src="./assets/medved.svg"
                alt="Plush Toy"
                className="h-16 sm:h-20 md:h-48"
                variants={plushToyVariants}
                animate="animate"
              />
              <motion.img
                src="./assets/superman-01.svg"
                alt="Superhero"
                className="h-16 sm:h-20 md:h-48 "
                variants={superheroVariants}
                animate="animate"
              />
            </div>
          )}
          {formData.ageGroup === "12-14" && (
            <div className="hidden md:flex flex-col items-center space-y-6 absolute right-0 transform translate-x-1/6">
              <motion.img
                src="./assets/ohnisko-01.svg"
                alt="Campfire"
                className="h-16 sm:h-20 md:h-48"
                variants={campfireVariants}
                animate="animate"
              />
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <Modal onClose={() => setShowModal(false)}>
          <motion.div
            className="bg-cream p-4 sm:p-8 rounded-3xl shadow-2xl border-4 border-primary max-w-[90%] sm:max-w-md w-full text-center"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <h3
              className="text-xl sm:text-3xl font-bold uppercase text-navy mb-2 sm:mb-4"
              style={{ fontFamily: "Baloo 2", fontWeight: 800 }}
            >
              Prihláška odoslaná!
            </h3>
            <p
              className="text-sm sm:text-lg md:text-xl text-navy mb-4 sm:mb-6"
              style={{ fontFamily: "Baloo 2", fontWeight: 500 }}
            >
              Ďakujeme, vaša prihláška bola úspešne odoslaná.
            </p>
            <motion.button
              onClick={() => setShowModal(false)}
              className="bg-primary text-cream py-2 sm:py-4 px-4 sm:px-8 rounded-xl font-bold uppercase tracking-wide shadow-lg text-sm sm:text-lg"
              whileHover={{ scale: 1.1, backgroundColor: "#F97316" }}
              whileTap={{ scale: 0.9 }}
            >
              Zavrieť
            </motion.button>
          </motion.div>
        </Modal>
      )}
    </section>
  );
};

export default Registration;
